/**
 * MessageBubbleMedia — CUADRO NEGRO Extracción Mínima Segura.
 *
 * Exporta `ImageGallery`, el sub-componente que renderiza imágenes
 * generadas por DamyxIA (base64) con navegación prev/next, thumbnails
 * y botón de descarga. Antes vivía dentro de MessageBubble.jsx.
 *
 * Reglas respetadas:
 * - NO se modifica la lógica original.
 * - NO se cambian estilos, JSX, ni orden de render.
 * - Usa el mismo `useLanguage()` que el original.
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const ImageGallery = ({ images, singleImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { language, t } = useLanguage();

  const allImages = images || (singleImage ? [singleImage] : []);
  const hasMultiple = allImages.length > 1;

  if (allImages.length === 0) return null;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const handleDownload = () => {
    try {
      const base64Data = allImages[currentIndex];
      // Convertir base64 a bytes binarios
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Crear Blob binario real
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Crear URL del blob y descargar
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `damyxia_imagen_${currentIndex + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <div className="mt-4">
      <div className="relative group">
        {/* Main Image */}
        <img
          src={`data:image/png;base64,${allImages[currentIndex]}`}
          alt={`Imagen ${currentIndex + 1} generada por DamyxIA`}
          className="max-w-full rounded-xl border border-zinc-700/50 shadow-lg"
          style={{ maxHeight: '500px', objectFit: 'contain' }}
        />

        {/* Navigation arrows for multiple images */}
        {hasMultiple && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Download button */}
        <button
          onClick={handleDownload}
          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          title={t('messages.downloadImage')}
        >
          <Download size={18} />
        </button>
      </div>

      {/* Thumbnails for multiple images */}
      {hasMultiple && (
        <div className="flex gap-2 mt-3 justify-center">
          {allImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex
                  ? 'border-indigo-500 ring-2 ring-indigo-500/50'
                  : 'border-zinc-700 hover:border-zinc-500'
              }`}
            >
              <img
                src={`data:image/png;base64,${img}`}
                alt={`Variación ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-zinc-500 mt-2 text-center">
        ✨ {hasMultiple
          ? (language === 'es'
              ? `Variación ${currentIndex + 1} de ${allImages.length}`
              : `Variation ${currentIndex + 1} of ${allImages.length}`)
          : t('messages.imageGeneratedBy')}
      </p>
    </div>
  );
};

export default ImageGallery;
